import { Box, Stack, Typography } from '@mui/material';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button/Button';
import { Checkbox } from '@/components/form/baseInputs/Checkbox/Checkbox';
import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { TypeaheadSelectInput } from '@/components/form/baseInputs/TypeaheadSelectInput/TypeaheadSelectInput';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { Loader } from '@/components/progress/Loader/Loader';
import { getUserFacingErrorMessages } from '@/graphql/errors';
import { SideDrawer } from '@/modules/common/SideDrawer/SideDrawer';
import { parseAssetOptionValue } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/hooks/useEntityAssetOptions';
import { CreateEntityShortFormModal } from '@/modules/entities/EntityShortFormModal/CreateEntityShortFormModal';
import { useGetEntityOptions } from '@/modules/entities/hooks/useGetEntityOptions';
import { COLORS } from '@/styles/tokens/colors';
import { EntityKind } from '@/types/schema';

import { INSURANCE_POLICY_ENTITY_TYPES } from '../../../entities.constants';
import { getEntityTypeFromEntityKind } from '../../../utils/getEntityTypeFromEntityKind';
import { GetEntityHoldingsForMoveDocument } from './graphql/GetEntityHoldingsForMove.generated';
import { useMoveHoldingsBetweenEntitiesMutation } from './graphql/MoveHoldingsBetweenEntities.generated';
import { useEntityHoldingsForMove } from './hooks/useEntityHoldingsForMove';

interface MoveAssetsToNewOwnerSideDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  householdId: string;
  /** The entity the assets/policies currently belong to. */
  sourceEntityId: string;
  /** Display name of the source entity, shown in the drawer subtitle. */
  sourceEntityName: string;
  /**
   * Called once after a successful move. The parent owns dismissing the drawer
   * and refreshing entity data here — the drawer does not also call `onClose`
   * on success, so hosts that map close and refresh to the same callback (e.g.
   * the presentation flow) don't refetch twice.
   */
  onMoved: () => void;
}

/** Adds or removes a value from a Set immutably. */
function toggleInSet(current: Set<string>, value: string): Set<string> {
  const next = new Set(current);
  if (next.has(value)) {
    next.delete(value);
  } else {
    next.add(value);
  }
  return next;
}

interface HoldingsCheckboxSectionProps {
  title: string;
  options: TypeaheadSelectInputOption<string>[];
  selected: Set<string>;
  onToggle: (value: string) => void;
  /** When true, rows are shown but not selectable (e.g. destination can't hold them). */
  disabled?: boolean;
}

/**
 * A titled section of checkable holdings, mirroring the "View all assets to
 * distribute" drawer rows. The row owns the click so the checkbox stays passive.
 */
function HoldingsCheckboxSection({
  title,
  options,
  selected,
  onToggle,
  disabled = false,
}: HoldingsCheckboxSectionProps) {
  if (options.length === 0) return null;

  return (
    <Box>
      <Box sx={{ bgcolor: COLORS.NAVY[100], px: 1.5, py: 0.75 }}>
        <Typography variant="body-xs">{title}</Typography>
      </Box>
      {options.map((option) => {
        const checked = selected.has(option.value);
        const valueSuffix = option.endLabel ? ` (${option.endLabel})` : '';
        return (
          // Rendering the row as a <label> wrapping the checkbox keeps the whole
          // row clickable while letting the native checkbox own toggling — so it
          // works with the keyboard (Space on the focused box) and a click
          // anywhere on the row fires exactly one change.
          <Stack
            key={option.value}
            component="label"
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              m: 0,
              px: 1.5,
              py: 1.5,
              borderBottom: `1px solid ${COLORS.NAVY[100]}`,
              cursor: disabled ? 'default' : 'pointer',
              opacity: disabled ? 0.5 : 1,
              '&:hover': disabled ? undefined : { bgcolor: COLORS.GRAY[50] },
            }}
          >
            <Typography variant="body-xs" sx={{ color: COLORS.TEXT.PRIMARY }}>
              {option.display}
              {valueSuffix}
            </Typography>
            <Checkbox
              hideLabel
              label={option.display}
              value={checked}
              disabled={disabled}
              onChange={() => onToggle(option.value)}
            />
          </Stack>
        );
      })}
    </Box>
  );
}

/**
 * Side drawer for moving manual assets and/or insurance policies from one entity
 * to another in the same household. The user picks a destination entity (with
 * inline "add new entity" support), selects holdings to move, and submits via
 * the `moveHoldingsBetweenEntities` mutation. If the destination entity kind
 * cannot hold insurance policies, that section is disabled and a callout
 * explains why.
 */
export function MoveAssetsToNewOwnerSideDrawer({
  isOpen,
  onClose,
  householdId,
  sourceEntityId,
  sourceEntityName,
  onMoved,
}: MoveAssetsToNewOwnerSideDrawerProps) {
  const { showFeedback } = useFeedback();
  const theme = useTheme();

  // The theme stacks the drawer layer above the modal layer, so a modal opened
  // from inside the drawer (the "Add new entity" flow) would render behind it.
  // Raise the modal layer above the drawer for that modal — and its own Select
  // menus, which derive their z-index from the theme — so it stacks on top.
  const elevatedModalTheme = useMemo(
    () => ({
      ...theme,
      zIndex: { ...theme.zIndex, modal: theme.zIndex.drawer + 1 },
    }),
    [theme]
  );

  const [destinationEntityId, setDestinationEntityId] = useState<string | null>(
    null
  );
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set());
  const [selectedPolicies, setSelectedPolicies] = useState<Set<string>>(
    new Set()
  );
  const [isCreateEntityOpen, setIsCreateEntityOpen] = useState(false);

  // Reset staged state whenever the drawer (re)opens so selections don't leak
  // across opens.
  useEffect(() => {
    if (isOpen) {
      setDestinationEntityId(null);
      setSelectedAssets(new Set());
      setSelectedPolicies(new Set());
    }
  }, [isOpen]);

  const { entityOptions } = useGetEntityOptions(householdId);
  const {
    assetOptions,
    policyOptions,
    loading: holdingsLoading,
    error: holdingsError,
  } = useEntityHoldingsForMove(sourceEntityId);

  // Every household entity except the one we're moving away from.
  const destinationOptions = useMemo<TypeaheadSelectInputOption<string>[]>(
    () =>
      entityOptions
        .filter((entity) => entity.value !== sourceEntityId)
        .map((entity) => ({ display: entity.display, value: entity.value })),
    [entityOptions, sourceEntityId]
  );

  const destinationEntity = entityOptions.find(
    (entity) => entity.value === destinationEntityId
  );

  // Until a destination is chosen we don't know its kind, so insurance stays
  // enabled; once chosen, gate on whether that entity kind can hold policies.
  const destinationSupportsInsurance = destinationEntity
    ? INSURANCE_POLICY_ENTITY_TYPES.includes(
        getEntityTypeFromEntityKind(destinationEntity.kind)
      )
    : true;

  // Insurance accounts ("life insurance holding groups") hold only policies and
  // have no concept of non-insurance assets (see getEntityValuationActionsBase),
  // so moving manual assets to one would strand them with no UI to remove them.
  // Until a destination is chosen we don't know its kind, so assets stay enabled.
  const destinationSupportsAssets = destinationEntity
    ? destinationEntity.kind !== EntityKind.InsurancePersonalAccount
    : true;

  // Drop staged holdings a disabled destination can't hold, so a disabled
  // section never contributes to the move.
  useEffect(() => {
    if (!destinationSupportsInsurance) {
      setSelectedPolicies(new Set());
    }
  }, [destinationSupportsInsurance]);

  useEffect(() => {
    if (!destinationSupportsAssets) {
      setSelectedAssets(new Set());
    }
  }, [destinationSupportsAssets]);

  const [moveHoldings, { loading: isMoving }] =
    useMoveHoldingsBetweenEntitiesMutation({
      // The moved holdings no longer belong to the source entity, so invalidate
      // its holdings query. The drawer unmounts on close and re-queries with
      // `cache-and-network` on reopen, which would otherwise briefly show the
      // already-moved holdings from the stale cache. This refetch is
      // deliberately *not* awaited: the move has already succeeded, so a failed
      // background refetch must not reject the mutation and mask that success.
      refetchQueries: [GetEntityHoldingsForMoveDocument],
      onCompleted: () => {
        showFeedback('Moved holdings to the new owner', { variant: 'success' });
        // Success signals through `onMoved` alone; the parent dismisses the
        // drawer and refreshes. Calling `onClose` too would double-refresh
        // hosts that alias the two callbacks.
        onMoved();
      },
      onError: (error) => {
        showFeedback(
          `Failed to move holdings: ${getUserFacingErrorMessages(error).join(' ')}`
        );
      },
    });

  const hasSelection = selectedAssets.size + selectedPolicies.size > 0;
  // Gate on the destination being *resolved* in `entityOptions`, not merely
  // selected. Right after creating a new entity inline we hold its id before
  // the options query refetches, during which `destinationSupportsInsurance`
  // can't be evaluated (and defaults to true). Requiring the resolved entity
  // keeps the move disabled until its kind is known, so policies can't be
  // submitted to an entity that turns out to be unable to hold them.
  const canMove = !!destinationEntity && hasSelection && !isMoving;

  const handleMove = () => {
    if (!destinationEntityId) return;

    // Option values are `prefix:id` composites; the raw id is what the mutation
    // expects for each list.
    const assetStaticIDs = Array.from(selectedAssets).map(
      (value) => parseAssetOptionValue(value).id
    );
    const insurancePolicyIDs = Array.from(selectedPolicies).map(
      (value) => parseAssetOptionValue(value).id
    );

    void moveHoldings({
      variables: {
        input: {
          sourceEntityID: sourceEntityId,
          destinationEntityID: destinationEntityId,
          assetStaticIDs,
          insurancePolicyIDs,
        },
      },
    });
  };

  const hasHoldings = assetOptions.length > 0 || policyOptions.length > 0;
  // Only treat the query error as fatal when it left us with nothing to show. A
  // `cache-and-network` refresh can error while still serving cached holdings —
  // in that case we keep showing the list rather than a misleading load error.
  const showLoadError = !!holdingsError && !hasHoldings;
  const noHoldings = !holdingsLoading && !holdingsError && !hasHoldings;

  const body = (
    <Box width="100%">
      <Stack spacing={2}>
        <TypeaheadSelectInput<string | null>
          label="Move to"
          placeholder="Select an entity"
          hideEmptyOption
          options={destinationOptions}
          value={destinationEntityId}
          onChange={(_e, value) => setDestinationEntityId(value ?? null)}
          addNewOption={{
            text: 'Add new entity',
            onClick: () => setIsCreateEntityOpen(true),
          }}
        />
        {!destinationSupportsAssets && (
          <Callout severity="info-low">
            The selected entity cannot hold assets.
          </Callout>
        )}
        {!destinationSupportsInsurance && (
          <Callout severity="info-low">
            The selected entity cannot hold life insurance policies.
          </Callout>
        )}
      </Stack>
      <Box sx={{ pt: 1 }}>
        {holdingsLoading && (
          <Stack alignItems="center" sx={{ py: 4 }}>
            <Loader circularProgressProps={{ size: 32 }} />
          </Stack>
        )}
        {showLoadError && (
          <Callout severity="error">
            We couldn&apos;t load this entity&apos;s holdings. Please close the
            drawer and try again.
          </Callout>
        )}
        {noHoldings && (
          <Typography variant="body-xs">
            This entity has no movable assets or insurance policies.
          </Typography>
        )}
        <Stack spacing={1}>
          <HoldingsCheckboxSection
            title="Assets"
            options={assetOptions}
            selected={selectedAssets}
            onToggle={(value) =>
              setSelectedAssets((prev) => toggleInSet(prev, value))
            }
            disabled={!destinationSupportsAssets}
          />
          <HoldingsCheckboxSection
            title="Life insurance policies"
            options={policyOptions}
            selected={selectedPolicies}
            onToggle={(value) =>
              setSelectedPolicies((prev) => toggleInSet(prev, value))
            }
            disabled={!destinationSupportsInsurance}
          />
        </Stack>
      </Box>
    </Box>
  );

  const footer = (
    <Stack direction="row" justifyContent="flex-end" spacing={1}>
      <Button
        variant="secondary"
        size="sm"
        onClick={onClose}
        data-testid="MoveAssetsToNewOwnerSideDrawer-cancel"
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        size="sm"
        disabled={!canMove}
        loading={isMoving}
        onClick={handleMove}
        data-testid="MoveAssetsToNewOwnerSideDrawer-move"
      >
        Move assets
      </Button>
    </Stack>
  );

  return (
    <>
      <SideDrawer
        isOpen={isOpen}
        onClose={onClose}
        heading={
          <Stack component="span" spacing={0.25}>
            <span>Move assets to new owner</span>
            {sourceEntityName && (
              <Typography component="span" variant="body-s">
                From {sourceEntityName}
              </Typography>
            )}
          </Stack>
        }
        body={body}
        footer={footer}
        bodyPropsSx={{ p: 2, flexDirection: 'column' }}
        data-testid="MoveAssetsToNewOwnerSideDrawer"
      />
      {isCreateEntityOpen && (
        <ThemeProvider theme={elevatedModalTheme}>
          <CreateEntityShortFormModal
            isOpen={isCreateEntityOpen}
            householdId={householdId}
            onClose={() => setIsCreateEntityOpen(false)}
            // Keep the user in the drawer and auto-select the entity they just
            // created as the destination, instead of the default redirect.
            onAfterCreate={(createdEntityId) => {
              setDestinationEntityId(createdEntityId);
              setIsCreateEntityOpen(false);
            }}
          />
        </ThemeProvider>
      )}
    </>
  );
}
